import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { CheckCircle, Clock, XCircle, ArrowLeft } from 'lucide-react';

const PaymentSuccess = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('checking');
  const [paymentData, setPaymentData] = useState(null);

  const sessionId = searchParams.get('session_id');
  const bookingId = searchParams.get('booking_id');
  const paymentType = searchParams.get('type') || 'deposit';

  const API = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    if (sessionId) {
      pollPaymentStatus(sessionId, 0);
    }
  }, [sessionId]);

  const pollPaymentStatus = async (sid, attempts) => {
    const maxAttempts = 8;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      setStatus('timeout');
      return;
    }

    try {
      const response = await fetch(`${API}/api/payments/status/${sid}`);
      if (!response.ok) throw new Error('Failed to check status');

      const data = await response.json();
      setPaymentData(data);

      if (data.payment_status === 'paid') {
        setStatus('success');
        return;
      } else if (data.status === 'expired') {
        setStatus('expired');
        return;
      }

      setStatus('processing');
      setTimeout(() => pollPaymentStatus(sid, attempts + 1), pollInterval);
    } catch {
      setStatus('error');
    }
  };

  const renderContent = () => {
    switch (status) {
      case 'checking':
      case 'processing':
        return (
          <div className="text-center" data-testid="payment-processing">
            <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-black mx-auto mb-6"></div>
            <h2 className="text-2xl font-bold mb-2">Processing Payment...</h2>
            <p className="text-gray-500">Please wait while we confirm your payment.</p>
          </div>
        );
      case 'success':
        return (
          <div className="text-center" data-testid="payment-success">
            <CheckCircle className="w-20 h-20 text-green-500 mx-auto mb-6" />
            <h2 className="text-3xl font-bold mb-2">Payment Successful!</h2>
            {paymentType === 'deposit' ? (
              <>
                <p className="text-gray-600 mb-2">Your 10% deposit has been received.</p>
                <p className="text-gray-500 text-sm mb-6">
                  Your tour booking is now confirmed. You'll pay the remaining balance after your tour.
                </p>
              </>
            ) : (
              <>
                <p className="text-gray-600 mb-2">Your final payment has been received.</p>
                <p className="text-gray-500 text-sm mb-6">Thank you for choosing We.Tour!</p>
              </>
            )}
            {paymentData && (
              <Card className="max-w-sm mx-auto mb-6">
                <CardContent className="p-4 text-left">
                  <p className="text-sm text-gray-500">Amount Paid</p>
                  <p className="text-2xl font-bold">${(paymentData.amount_total / 100).toFixed(2)}</p>
                  <p className="text-xs text-gray-400 mt-1">Booking ID: {bookingId}</p>
                </CardContent>
              </Card>
            )}
            <div className="flex gap-3 justify-center">
              <Button onClick={() => navigate('/')} data-testid="go-home-btn">
                <ArrowLeft className="w-4 h-4 mr-2" /> Back to Home
              </Button>
              {paymentType === 'deposit' && bookingId && (
                <Button variant="outline" onClick={() => navigate(`/payment/${bookingId}`)} data-testid="view-payment-btn">
                  View Payment Details
                </Button>
              )}
            </div>
          </div>
        );
      case 'expired':
        return (
          <div className="text-center" data-testid="payment-expired">
            <Clock className="w-20 h-20 text-yellow-500 mx-auto mb-6" />
            <h2 className="text-2xl font-bold mb-2">Session Expired</h2>
            <p className="text-gray-500 mb-6">Your payment session has expired. Please try booking again.</p>
            <Button onClick={() => navigate('/guides')}>Browse Guides</Button>
          </div>
        );
      default:
        return (
          <div className="text-center" data-testid="payment-error">
            <XCircle className="w-20 h-20 text-red-500 mx-auto mb-6" />
            <h2 className="text-2xl font-bold mb-2">Payment Status Unknown</h2>
            <p className="text-gray-500 mb-6">We couldn't confirm your payment. If you were charged, please contact support.</p>
            <Button onClick={() => navigate('/')}>Back to Home</Button>
          </div>
        );
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      <main className="max-w-2xl mx-auto px-4 py-20">
        {renderContent()}
      </main>
      <Footer />
    </div>
  );
};

export default PaymentSuccess;
